import React, { useCallback, useEffect, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import {
  addDoc,
  collection,
  getDocs,
  query,
  Timestamp,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { BsTextLeft } from 'react-icons/bs';
import { FaEdit, FaInstagram, FaMapPin } from 'react-icons/fa';
import { FiPhone } from 'react-icons/fi';
import { MdPhoneAndroid } from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import { GroupBase, MultiValue, OptionsOrGroups } from 'react-select';
import * as Yup from 'yup';

import Button from 'components/Button';
import CustomSelect from 'components/CustomSelect';
import DynamicInput from 'components/DynamicInput';
import DynamicTextArea from 'components/DynamicTextArea';
import { useUserData } from 'hooks/auth';
import Category from 'interfaces/Category';
import Service from 'interfaces/Service';
import { db } from 'services/firebase';
import getValidationErrors from 'utils/getValidationErrors';

import {
  Container,
  ServiceForm,
  ServiceFormFields,
  ServiceFormSection,
} from './styles';

interface ServiceFormData {
  name: string;
  cellphone: string;
  phone: string;
  instagram: string;
  description: string;
  location: string;
}

const AddService: React.FC = () => {
  const history = useHistory();
  const { user } = useUserData();

  const formRef = useRef<FormHandles>(null);

  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [options, setOptions] = useState<
    OptionsOrGroups<Category, GroupBase<Category>> | undefined
  >([]);

  const updateCategory = (newOptions: MultiValue<Category>): void => {
    setSelectedCategories(newOptions as Category[]);
  };

  const handleSubmit = useCallback(
    async (data: ServiceFormData) => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape(
          {
            name: Yup.string().required('Nome obrigatório'),
            cellphone: Yup.string().when('phone', {
              is: (phone: string) => !phone || phone.length === 0,
              then: Yup.string().required(
                'Informe pelo menos um celular ou um telefone fixo',
              ),
            }),
            phone: Yup.string().when('cellphone', {
              is: (cellphone: string) => !cellphone || cellphone.length === 0,
              then: Yup.string().required(
                'Informe pelo menos um celular ou um telefone fixo',
              ),
            }),
          },
          [['cellphone', 'phone']],
        );

        await schema.validate(data, {
          abortEarly: false,
        });

        if (selectedCategories.length === 0) {
          toast.error('Você precisa selecionar pelo menos uma categoria');
          return;
        }

        if (selectedCategories.length > 3) {
          toast.error('Você pode selecionar no máximo 3 categorias');
          return;
        }

        if (!user) {
          toast.error('Você precisa estar logado para cadastrar um serviço');
          return;
        }

        const service: Service = {
          name: data.name,
          description: data.description,
          categories: selectedCategories,
          cellphones: data.cellphone === '' ? [] : [data.cellphone],
          excluded: false,
          instagram: data.instagram,
          location: data.location,
          numReviews: 0,
          phones: data.phone === '' ? [] : [data.phone],
          reviews: [],
          dateAdded: Timestamp.now(),
          dateModified: Timestamp.now(),
          organic: true,
          addedByUserId: user.uid,
        };

        const serviceRef = await addDoc(collection(db, 'services'), service);

        toast.success('Serviço criado com sucesso!');

        history.push(`/services/${serviceRef.id}`);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          toast.error('Há erros de validação. Corrija-os abaixo', {
            duration: 2000,
          });

          formRef.current?.setErrors(errors);
          return;
        }

        toast.error(`Erro ao criar serviço. Erro: ${(err as Error).message}`, {
          duration: 2000,
        });
      }
    },
    [history, selectedCategories, user],
  );

  useEffect(() => {
    const loadCategories = async (): Promise<void> => {
      const q = query(collection(db, 'categories'));

      const snapshot = await getDocs(q);

      setOptions(snapshot.docs.map((document) => document.data() as Category));
    };

    loadCategories();
  }, []);

  return (
    <Container>
      <ServiceForm ref={formRef} onSubmit={handleSubmit}>
        <h2>Cadastrar serviço</h2>
        <ServiceFormFields>
          <ServiceFormSection>
            <h3>Informações gerais</h3>
            <DynamicInput
              name="name"
              placeholder="Nome do serviço"
              icon={FaEdit}
            />
            <CustomSelect
              options={options}
              defaultValue={selectedCategories}
              onChange={updateCategory}
              getOptionValue={(category) => category.id}
              formatOptionLabel={(category) => category.readableName}
              isSearchable
              isMulti
              placeholder="Selecionar Categoria"
            />
            <DynamicTextArea
              name="description"
              placeholder="Descrição breve do serviço"
              icon={BsTextLeft}
            />
            <DynamicInput
              name="location"
              placeholder="Endereço"
              icon={FaMapPin}
            />
          </ServiceFormSection>

          <ServiceFormSection>
            <h3>Contatos</h3>
            <DynamicInput
              name="cellphone"
              placeholder="Celular"
              icon={MdPhoneAndroid}
            />
            <DynamicInput name="phone" placeholder="Telefone" icon={FiPhone} />
            <DynamicInput
              name="instagram"
              placeholder="Usuário no Instagram"
              icon={FaInstagram}
            />
          </ServiceFormSection>
        </ServiceFormFields>

        <Button type="submit" style={{ width: '250px' }}>
          Cadastrar
        </Button>
      </ServiceForm>
    </Container>
  );
};

export default AddService;
